/**
 * Keys used by the app for auth and persisted state.
 * Used when self-healing from "Request Header Or Cookie Too Large" (Nginx 400).
 *
 * Validação: o front NÃO grava cookies (idioma só em localStorage; config do backend
 * fica em localStorage/memória). Tokens e dados sensíveis só em localStorage.
 */
export const APP_STORAGE_KEYS = [
  'token',
  'refreshToken',
  'companyId',
  'cshow',
  'loginOrigin',
  'profileImage',
  'companyDueDate',
  'sendSignMessage',
  'dueDate',
  'variables',
  'volume',
  'persistentSignMessage',
  'appName',
  'primaryColorLight',
  'primaryColorDark',
  'preferredTheme',
  'frontendVersion',
  'version',
  'user',
  'i18nextLng',
  'whaticket_notification_permission',
  'pingochat_had_cookie_error',
];

const SESSION_FLAG = 'pingochat_header_too_large_shown';

/** Keys kept when clearing storage to preserve login (no logout). */
export const AUTH_PRESERVED_KEYS = ['token', 'refreshToken', 'companyId', 'user'];

/**
 * Clear app storage but keep auth keys so the user stays logged in.
 * Use when we only want to reduce stored data (e.g. cookie/header too large) without logging out.
 */
export function clearAppStoragePreservingAuth(options = {}) {
  const { clearCaches = true, unregisterServiceWorkers = true } = options;
  const keep = new Set(AUTH_PRESERVED_KEYS);

  try {
    APP_STORAGE_KEYS.forEach((key) => {
      if (keep.has(key)) return;
      try {
        localStorage.removeItem(key);
      } catch (_) {
        // ignore
      }
    });
    try {
      sessionStorage.clear();
    } catch (_) {
      // ignore
    }

    if (clearCaches && typeof caches !== 'undefined') {
      caches.keys().then((names) => {
        names.forEach((name) => caches.delete(name));
      });
    }

    if (unregisterServiceWorkers && typeof navigator !== 'undefined' && navigator.serviceWorker) {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        registrations.forEach((r) => r.unregister());
      });
    }
  } catch (_) {
    // best effort
  }
}

/**
 * Clear app localStorage keys, sessionStorage, and optionally caches/service workers.
 * Call before reload when user chooses "Limpar dados do site e recarregar" (full clear = logout).
 * JS cannot remove HttpOnly cookies for api.pingochat.com.br; user must clear those manually.
 */
export function clearAppStorage(options = {}) {
  const { clearCaches = true, unregisterServiceWorkers = true } = options;

  try {
    APP_STORAGE_KEYS.forEach((key) => {
      try {
        localStorage.removeItem(key);
      } catch (_) {
        // ignore
      }
    });
    try {
      sessionStorage.clear();
    } catch (_) {
      // ignore
    }

    if (clearCaches && typeof caches !== 'undefined') {
      caches.keys().then((names) => {
        names.forEach((name) => caches.delete(name));
      });
    }

    if (unregisterServiceWorkers && typeof navigator !== 'undefined' && navigator.serviceWorker) {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        registrations.forEach((r) => r.unregister());
      });
    }
  } catch (_) {
    // best effort
  }
}

/**
 * Mark that we have shown the cookie/header-too-large modal this session (avoid loops).
 */
export function setHeaderTooLargeModalShown() {
  try {
    sessionStorage.setItem(SESSION_FLAG, '1');
  } catch (_) {
    // ignore
  }
}

/**
 * Check if we already showed the modal this session.
 */
export function wasHeaderTooLargeModalShown() {
  try {
    return sessionStorage.getItem(SESSION_FLAG) === '1';
  } catch (_) {
    return false;
  }
}

/**
 * Clear the "modal already shown" flag (for dev/testing only).
 */
export function clearHeaderTooLargeModalShown() {
  try {
    sessionStorage.removeItem(SESSION_FLAG);
  } catch (_) {
    // ignore
  }
}

const HAD_COOKIE_ERROR_KEY = 'pingochat_had_cookie_error';

/** Marca que já ocorreu erro 400 header/cookie too large (para mostrar o modal ao entrar de novo). */
export function setHadCookieError() {
  try {
    localStorage.setItem(HAD_COOKIE_ERROR_KEY, '1');
  } catch (_) {
    // ignore
  }
}

/** Verifica se já ocorreu o erro (mostrar modal ao entrar no sistema). */
export function hadCookieError() {
  try {
    return localStorage.getItem(HAD_COOKIE_ERROR_KEY) === '1';
  } catch (_) {
    return false;
  }
}
